import { useEffect, useState } from 'react'
import { FiX } from 'react-icons/fi'

import { renameSession } from '@/api/agent'
import { useChatStore } from '@/store'

interface RenameModalProps {
  isOpen: boolean
  sessionId: string | null
  initialTitle: string
  onClose: () => void
}

export default function RenameModal({ isOpen, sessionId, initialTitle, onClose }: RenameModalProps) {
  const [title, setTitle] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const { fetchSessions } = useChatStore()

  useEffect(() => {
    if (isOpen) {
      setTitle(initialTitle)
    }
  }, [isOpen, initialTitle])

  if (!isOpen) return null

  const handleSubmit = async () => {
    if (!sessionId || !title.trim()) return

    setIsSubmitting(true)
    try {
      await renameSession({
        sessionId,
        title: title.trim()
      })
      // Refresh session list
      fetchSessions()
      onClose()
    } catch (error) {
      console.error('Failed to rename session', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleClose = () => {
    onClose()
  }

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 px-4 backdrop-blur-sm transition-opacity duration-300">
      <div className="relative w-full max-w-[400px] overflow-hidden rounded-2xl bg-white p-6 shadow-2xl transition-all duration-300 animate-in fade-in zoom-in-95">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-bold text-gray-900">编辑对话名称</h2>
          <button onClick={handleClose} className="rounded-full p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors">
            <FiX className="h-5 w-5" />
          </button>
        </div>

        <div className="mb-6">
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={50}
            autoFocus
            placeholder="请输入对话名称"
            className="w-full px-4 py-2.5 text-sm text-gray-900 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 placeholder:text-gray-400"
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleSubmit()
              }
            }}
          />
        </div>

        <div className="flex items-center justify-end gap-3">
          <button onClick={handleClose} className="px-6 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
            取消
          </button>
          <button
            onClick={handleSubmit}
            disabled={!title.trim() || isSubmitting}
            className={`px-6 py-2 text-sm font-medium text-white rounded-lg transition-colors ${!title.trim() || isSubmitting ? 'bg-blue-300 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'}`}
          >
            {isSubmitting ? '提交中...' : '确定'}
          </button>
        </div>
      </div>
    </div>
  )
}
